// Fixed FaTimes import issue
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SERVER } from '../../../../config/config';
import { usePermission } from '../../../../hooks/usePermission';
import { 
  FaSearch, FaFilter, FaEye, FaEdit, FaTrash, FaDownload,
  FaCalendarAlt, FaUser, FaEnvelope, FaPhone, FaBuilding,
  FaDollarSign, FaFileAlt, FaPaperclip, FaStickyNote, FaTimes,
  FaCamera, FaVideo, FaMapMarkerAlt, FaFacebook, FaInstagram, FaTiktok
} from 'react-icons/fa';

const Consultations = () => {
  const { can } = usePermission();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const canEdit = can('submissions.edit');
  const canDelete = can('submissions.delete');

  const statusOptions = [
    { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-800' },
    { value: 'in_progress', label: 'In Progress', color: 'bg-purple-100 text-purple-800' },
    { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' }
  ];

  const serviceOptions = [
    { value: 'wetekup', label: 'WeTekup', icon: '💻' },
    { value: 'wecapture', label: 'WeCapture', icon: '📸' }
  ];

  useEffect(() => {
    fetchConsultations();
  }, [currentPage, searchTerm, filterStatus, filterService]);

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...(filterStatus !== 'all' && { status: filterStatus }),
        ...(filterService !== 'all' && { service: filterService })
      };

      const response = await axios.get(`${SERVER}/consultations/admin/list`, {
        params,
        withCredentials: true
      });

      setConsultations(response.data.consultations);
      setTotalPages(response.data.pagination.pages);
    } catch (error) {
      console.error('Error fetching consultations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (consultationId, newStatus) => {
    if (!canEdit) return;
    
    try {
      await axios.put(`${SERVER}/consultations/admin/${consultationId}/status`, {
        status: newStatus
      }, {
        withCredentials: true
      });
      fetchConsultations();
    } catch (error) {
      console.error('Error updating consultation status:', error);
    }
  };

  const handleNotesUpdate = async (consultationId, notes) => {
    if (!canEdit) return;
    
    try {
      await axios.put(`${SERVER}/consultations/admin/${consultationId}/status`, {
        status: selectedConsultation.status,
        notes: notes
      }, {
        withCredentials: true
      });
      fetchConsultations();
    } catch (error) {
      console.error('Error updating consultation notes:', error);
    }
  };

  const handleDelete = async (consultation) => {
    if (!canDelete) return;
    
    if (window.confirm(`Are you sure you want to delete the consultation from "${consultation.name}"?`)) {
      try {
        await axios.delete(`${SERVER}/consultations/admin/${consultation.id}`, {
          withCredentials: true
        });
        fetchConsultations();
      } catch (error) {
        console.error('Error deleting consultation:', error);
      }
    }
  };

  const handleViewDetails = (consultation) => {
    setSelectedConsultation(consultation);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedConsultation(null);
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.color : 'bg-slate-100 text-slate-800';
  };

  const getStatusLabel = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.label : status;
  };

  const getServiceLabel = (service) => {
    const serviceOption = serviceOptions.find(opt => opt.value === service);
    return serviceOption ? serviceOption.label : service;
  };

  const getServiceIcon = (service) => {
    const serviceOption = serviceOptions.find(opt => opt.value === service);
    return serviceOption ? serviceOption.icon : '🔧';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const parseSocialMediaLinks = (linksString) => {
    try {
      return typeof linksString === 'string' ? JSON.parse(linksString) : linksString || {};
    } catch {
      return {};
    }
  };

  const parseShootingLocation = (locationString) => {
    try {
      return typeof locationString === 'string' ? JSON.parse(locationString) : locationString || [];
    } catch {
      return [];
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Consultation Requests</h2>
          <p className="text-slate-600">Manage consultation booking requests from clients</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-4 py-2 rounded-lg transition-colors"
          >
            <FaDownload />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search consultations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              {statusOptions.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
            <select
              value={filterService}
              onChange={(e) => setFilterService(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Services</option>
              {serviceOptions.map(service => (
                <option key={service.value} value={service.value}>{service.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Consultations Table */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 font-semibold text-slate-700">Client</th>
                <th className="text-left p-4 font-semibold text-slate-700">Service</th>
                <th className="text-left p-4 font-semibold text-slate-700">Activity Field</th>
                <th className="text-left p-4 font-semibold text-slate-700">Date</th>
                <th className="text-left p-4 font-semibold text-slate-700">Status</th>
                <th className="text-right p-4 font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {consultations.map((consultation) => (
                <tr key={consultation.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="p-4">
                    <div>
                      <h3 className="font-semibold text-slate-800">{consultation.name}</h3>
                      <div className="text-sm text-slate-500 space-y-1">
                        {consultation.job_position && (
                          <div className="text-xs text-slate-400">{consultation.job_position}</div>
                        )}
                        <div className="flex items-center gap-1">
                          <FaEnvelope className="text-xs" />
                          <span>{consultation.email}</span>
                        </div>
                        {consultation.phone && (
                          <div className="flex items-center gap-1">
                            <FaPhone className="text-xs" />
                            <span>{consultation.phone}</span>
                          </div>
                        )}
                        {consultation.company && (
                          <div className="flex items-center gap-1">
                            <FaBuilding className="text-xs" />
                            <span>{consultation.company}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getServiceIcon(consultation.service)}</span>
                      <span className="text-sm text-slate-600">
                        {getServiceLabel(consultation.service)}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-slate-600">
                      {consultation.activity_field || 'Not specified'}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="text-sm text-slate-600">
                      <div>{formatDate(consultation.created_at)}</div>
                      {consultation.shooting_date && (
                        <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          <FaCalendarAlt className="text-xs" />
                          Shooting: {new Date(consultation.shooting_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <select
                      value={consultation.status}
                      onChange={(e) => handleStatusChange(consultation.id, e.target.value)}
                      disabled={!canEdit}
                      className={`px-3 py-1 rounded-full text-sm font-medium border-0 ${getStatusColor(consultation.status)} ${
                        canEdit ? 'cursor-pointer' : 'cursor-not-allowed'
                      }`}
                    >
                      {statusOptions.map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleViewDetails(consultation)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => handleDelete(consultation)}
                        disabled={!canDelete}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-200">
            <div className="text-sm text-slate-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showModal && selectedConsultation && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  Consultation Request Details
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Client Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                    <FaUser />
                    Client Information
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-medium text-slate-600">Name:</span>
                      <span className="ml-2 text-slate-800">{selectedConsultation.name}</span>
                    </div>
                    {selectedConsultation.job_position && (
                      <div>
                        <span className="font-medium text-slate-600">Job Position:</span>
                        <span className="ml-2 text-slate-800">{selectedConsultation.job_position}</span>
                      </div>
                    )}
                    <div>
                      <span className="font-medium text-slate-600">Email:</span>
                      <span className="ml-2 text-slate-800">{selectedConsultation.email}</span>
                    </div>
                    {selectedConsultation.phone && (
                      <div>
                        <span className="font-medium text-slate-600">Phone:</span>
                        <span className="ml-2 text-slate-800">{selectedConsultation.phone}</span>
                      </div>
                    )}
                    {selectedConsultation.company && (
                      <div>
                        <span className="font-medium text-slate-600">Company:</span>
                        <span className="ml-2 text-slate-800">{selectedConsultation.company}</span>
                      </div>
                    )}
                    {selectedConsultation.activity_field && (
                      <div>
                        <span className="font-medium text-slate-600">Activity Field:</span>
                        <span className="ml-2 text-slate-800">{selectedConsultation.activity_field}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                    <FaFileAlt />
                    Request Details
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-medium text-slate-600">Service:</span>
                      <span className="ml-2 text-slate-800 flex items-center gap-2">
                        <span>{getServiceIcon(selectedConsultation.service)}</span>
                        {getServiceLabel(selectedConsultation.service)}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-slate-600">Status:</span>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedConsultation.status)}`}>
                        {getStatusLabel(selectedConsultation.status)}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-slate-600">Preferred Contact:</span>
                      <span className="ml-2 text-slate-800">{selectedConsultation.preferred_contact}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Project Description */}
              {selectedConsultation.description && (
                <div>
                  <h4 className="font-semibold text-slate-800 mb-3">Project Description</h4>
                  <div className="bg-slate-50 rounded-xl p-4 text-slate-700">
                    {selectedConsultation.description}
                  </div>
                </div>
              )}

              {/* Detailed Needs */}
              {selectedConsultation.detailed_needs && (
                <div>
                  <h4 className="font-semibold text-slate-800 mb-3">Detailed Needs</h4>
                  <div className="bg-slate-50 rounded-xl p-4 text-slate-700">
                    {selectedConsultation.detailed_needs}
                  </div>
                </div>
              )}

              {/* Social Media Links */}
              {selectedConsultation.social_media_links && Object.keys(parseSocialMediaLinks(selectedConsultation.social_media_links)).length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-800 mb-3">Social Media Links</h4>
                  <div className="space-y-2">
                    {Object.entries(parseSocialMediaLinks(selectedConsultation.social_media_links)).map(([platform, url]) => (
                      url && (
                        <div key={platform} className="flex items-center gap-2">
                          {platform === 'facebook' && <FaFacebook className="text-blue-600" />}
                          {platform === 'instagram' && <FaInstagram className="text-pink-600" />}
                          {platform === 'website' && <FaTiktok className="text-black" />}
                          <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {url}
                          </a>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* WeCapture Specific Details */}
              {selectedConsultation.service === 'wecapture' && (
                <div className="border-t border-slate-200 pt-6">
                  <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <FaCamera />
                    WeCapture Studio Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3 text-sm">
                      {selectedConsultation.studio_needs && (
                        <div>
                          <span className="font-medium text-slate-600">Studio Needs:</span>
                          <span className="ml-2 text-slate-800">{selectedConsultation.studio_needs}</span>
                        </div>
                      )}
                      {selectedConsultation.model_included && (
                        <div>
                          <span className="font-medium text-slate-600">Model/Mannequin:</span>
                          <span className="ml-2 text-slate-800">{selectedConsultation.model_included}</span>
                        </div>
                      )}
                      {selectedConsultation.shooting_type && (
                        <div>
                          <span className="font-medium text-slate-600">Shooting Type:</span>
                          <span className="ml-2 text-slate-800">{selectedConsultation.shooting_type}</span>
                        </div>
                      )}
                    </div>
                    <div className="space-y-3 text-sm">
                      {selectedConsultation.shooting_location && parseShootingLocation(selectedConsultation.shooting_location).length > 0 && (
                        <div>
                          <span className="font-medium text-slate-600">Shooting Locations:</span>
                          <div className="ml-2 space-y-1">
                            {parseShootingLocation(selectedConsultation.shooting_location).map((location, index) => (
                              <div key={index} className="flex items-center gap-1 text-slate-800">
                                <FaMapMarkerAlt className="text-xs text-slate-400" />
                                {location}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedConsultation.shooting_date && (
                        <div>
                          <span className="font-medium text-slate-600">Expected Shooting Date:</span>
                          <span className="ml-2 text-slate-800">
                            {new Date(selectedConsultation.shooting_date).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Product Photo */}
              {selectedConsultation.product_photo && (
                <div>
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <FaPaperclip />
                    Product Photo
                  </h4>
                  <a
                    href={`${SERVER}${selectedConsultation.product_photo}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-4 py-2 rounded-lg transition-colors inline-flex"
                  >
                    <FaDownload />
                    <span>View Product Photo</span>
                  </a>
                </div>
              )}

              {/* Notes */}
              <div>
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <FaStickyNote />
                  Internal Notes
                </h4>
                <textarea
                  value={selectedConsultation.admin_notes || ''}
                  onChange={(e) => {
                    setSelectedConsultation(prev => ({
                      ...prev,
                      admin_notes: e.target.value
                    }));
                  }}
                  onBlur={(e) => handleNotesUpdate(selectedConsultation.id, e.target.value)}
                  disabled={!canEdit}
                  placeholder="Add internal notes about this consultation..."
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:cursor-not-allowed"
                />
              </div>

              {/* Timestamps */}
              <div className="text-xs text-slate-500 border-t border-slate-200 pt-4">
                <div>Submitted: {formatDate(selectedConsultation.created_at)}</div>
                {selectedConsultation.updated_at !== selectedConsultation.created_at && (
                  <div>Last updated: {formatDate(selectedConsultation.updated_at)}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Consultations;